import * as React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

export interface FloatingLabelInputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
  id: string;
}

/**
 * FloatingLabelInput - Input with animated floating label
 *
 * A text input component with a label that floats up when the input is focused or has content.
 * Provides a modern, space-efficient UX pattern.
 *
 * @example
 * ```tsx
 * <FloatingLabelInput
 *   id="email"
 *   label="Email Address"
 *   type="email"
 *   value={email}
 *   onChange={(e) => setEmail(e.target.value)}
 * />
 * ```
 */
const FloatingLabelInput = React.forwardRef<
  HTMLInputElement,
  FloatingLabelInputProps
>(({ className, label, id, value, ...props }, ref) => {
  const hasValue = value !== undefined && value !== null && value !== '';

  return (
    <div className="relative">
      <Input
        id={id}
        ref={ref}
        value={value}
        placeholder=" "
        className={cn('peer placeholder-transparent focus-visible:ring-1', className)}
        {...props}
      />
      <Label
        htmlFor={id}
        className={cn(
          'absolute left-3 transition-all duration-200 pointer-events-none',
          hasValue
            ? '-top-2.5 text-xs bg-background px-1 text-muted-foreground'
            : 'top-1/2 -translate-y-1/2 text-sm text-muted-foreground peer-focus:-top-2.5 peer-focus:translate-y-0 peer-focus:text-xs peer-focus:bg-background peer-focus:px-1 peer-focus:text-muted-foreground'
        )}
      >
        {label}
      </Label>
    </div>
  );
});

FloatingLabelInput.displayName = 'FloatingLabelInput';

export { FloatingLabelInput };
